import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ローカルストレージからセッションを同期的に読み込む（高速起動のため）
function getInitialUser(): User | null {
  try {
    const stored = localStorage.getItem('management-os-auth');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const session = parsed?.currentSession ?? parsed;
    if (!session?.user) return null;
    // アクセストークンの有効期限チェック
    const expiresAt = session.expires_at;
    if (expiresAt && Date.now() / 1000 > expiresAt + 60) return null;
    return session.user as User;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // 同期的にローカルストレージからユーザー情報を取得（初期ローディングを排除）
  const [user, setUser] = useState<User | null>(getInitialUser);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // バックグラウンドでセッションを検証・リフレッシュ
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error && (
        error.message?.includes('Refresh Token Not Found') ||
        error.message?.includes('Invalid Refresh Token')
      )) {
        supabase.auth.signOut();
        setUser(null);
      } else {
        setUser(session?.user ?? null);
      }
    });

    // 認証状態の変更を監視
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        event === 'SIGNED_OUT' ||
        (event === 'TOKEN_REFRESHED' && !session)
      ) {
        setUser(null);
        return;
      }

      if (!session && event !== 'INITIAL_SESSION') {
        setUser(null);
        return;
      }

      setUser(session?.user ?? null);
    });

    // グローバルなSupabaseエラーをキャッチしてトークン無効時に自動ログアウト
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event?.reason?.message ?? '';
      if (
        message.includes('Refresh Token Not Found') ||
        message.includes('Invalid Refresh Token')
      ) {
        supabase.auth.signOut();
        setUser(null);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
